import useStaffAccountLookup from "../Staff/useStaffAccountLookup";
import StatusAlert from "../../Components/Common/StatusAlert";
import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import { Plus } from "lucide-react";
import { Button } from "../../shadcn/components/ui/button";
import Drawer from "../../Components/Common/DrawerLayout/Drawer";
import SearchCombobox from "../../Components/Common/SearchCombobox";
import Form from "../../Components/Form/Form";
import TextFieldRenderer from "../../Components/Form/TextFieldRenderer";
import {
  formValidator,
  isInvalidEmail,
  isInvalidPhoneNumber,
} from "../../Components/Form/validators";
import SelectRenderer from "../../Components/Form/SelectRenderer";
import { GET_CLUBS_SA, REGISTER_USER_SA } from "./SuperAdminService";
import { REGISTER_CLUB_USER } from "../../graphql/clubGraphql";
import { GET_CLUB_TEAMS } from "../../graphql/postGraphql";
import { AuthContext } from "../../context/AuthContext";
import { useTranslation } from "../../context/Internationalization";
import useGraphql from "../../Hooks/useGraphql";
import useLog from "../../Hooks/useLog";
import { isSuperAdmin } from "../../utility/Utils";
import { getStorage } from "../../auth/utils";
import { USER_ROLES } from "../../utility/Enum";
import { getMissingStaffIdentityErrors } from "./staffRegistrationIdentity";
import { readStaffStorageScope } from "../Staff/staffUtils";
import {
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../shadcn/components/ui/sheet";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../shadcn/components/ui/card";
import { cn } from "../../shadcn/lib/utils";
import { toast } from "sonner";

const staffRoles = [
  USER_ROLES.COACH,
  USER_ROLES.TEAM_ADMIN,
  USER_ROLES.CLUB_ADMIN,
];
const needsTeams = (role) =>
  role === USER_ROLES.COACH || role === USER_ROLES.TEAM_ADMIN;

const ClubSearchRenderer = ({
  label,
  name,
  onChange,
  disabled,
  error,
  storageScope,
  expectedFirebaseUid,
}) => {
  const [term, setTerm] = useState("");
  const [selectedClub, setSelectedClub] = useState(null);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { axiosGraphQL } = useGraphql();
  const services = useRef();
  services.current = { axiosGraphQL };

  useEffect(() => {
    let active = true;
    const isCurrent = () =>
      active && readStaffStorageScope(getStorage()) === storageScope;
    setOptions([]);
    setLoading(false);
    if (term.trim().length < 3 || selectedClub) return;
    const timer = setTimeout(() => {
      if (!isCurrent() || !expectedFirebaseUid) return;
      setLoading(true);
      services.current
        .axiosGraphQL(
          { query: GET_CLUBS_SA, variables: { term: term.trim() } },
          { expectedFirebaseUid }
        )
        .then((response) => {
          if (isCurrent()) setOptions(response.data.data.getClubsSA || []);
        })
        .catch((searchError) => {
          if (!isCurrent()) return;
          toast.error(searchError.message);
        })
        .finally(() => {
          if (isCurrent()) setLoading(false);
        });
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [term, selectedClub, storageScope, expectedFirebaseUid]);

  return (
    <SearchCombobox
      label={label}
      error={error}
      disabled={disabled}
      filter={null}
      options={options}
      loading={loading}
      getOptionLabel={(club) => club?.name || ""}
      getOptionKey={(club) => club._id}
      isOptionEqualToValue={(club, selected) => club._id === selected._id}
      inputValue={term}
      onInputValueChange={(input, { reason }) => {
        if (reason !== "input-change") return;
        setTerm(input);
        setSelectedClub(null);
        onChange({ target: { name, value: "" } });
      }}
      value={selectedClub}
      showClear
      onValueChange={(club) => {
        setSelectedClub(club);
        setTerm(club?.name || "");
        onChange({ target: { name, value: club?._id || "" } });
      }}
    />
  );
};

const schema = (
  selectedRole,
  t,
  globalSuperAdmin,
  teams,
  multipleTeams,
  clubId,
  storageScope,
  expectedFirebaseUid
) =>
  [
    {
      label: t("firstName"),
      name: "firstName",
      validators: [
        {
          validator: (value) => !value?.trim(),
          text: (label) => `${label} ${t("cannot_be_empty")}`,
        },
        {
          validator: (value) => value?.trim().length > 100,
          text: () => t("registration_required_name"),
        },
      ],
      maxLength: 100,
      renderer: TextFieldRenderer,
      row: 1,
    },
    {
      label: t("lastName"),
      name: "lastName",
      maxLength: 100,
      validators: [
        {
          validator: (value) => value?.trim().length > 100,
          text: () => t("registration_required_name"),
        },
      ],
      renderer: TextFieldRenderer,
      row: 2,
    },
    {
      label: t("mobile"),
      name: "mobile",
      validators: [
        {
          validator: (value) =>
            Boolean(value) &&
            (!/^\+[1-9]\d{6,14}$/.test(value) || isInvalidPhoneNumber(value)),
          text: (_label) => `${t("error_invalid_phone_number")}`,
        },
      ],
      renderer: TextFieldRenderer,
      row: 3,
    },
    {
      label: t("email"),
      name: "email",
      validators: [
        {
          validator: isInvalidEmail,
          text: (_label) => `${t("error_invalid_email")}`,
        },
      ],
      renderer: TextFieldRenderer,
      row: 4,
    },
    {
      label: t("role"),
      name: "role",
      validators: [
        {
          validator: (value) => !staffRoles.includes(value),
          text: (label) => `${label} ${t("cannot_be_empty")}`,
        },
      ],
      options: [
        { text: t("coach"), value: USER_ROLES.COACH },
        { text: t("team_admin"), value: USER_ROLES.TEAM_ADMIN },
        { text: t("club_admin"), value: USER_ROLES.CLUB_ADMIN },
      ],
      renderer: SelectRenderer,
      row: 5,
    },
    globalSuperAdmin && {
      label: t("club_page_title"),
      name: "clubId",
      validators: [
        {
          validator: (value) => !value,
          text: (label) => `${label} ${t("cannot_be_empty")}`,
        },
      ],
      renderer: (props) => (
        <ClubSearchRenderer
          {...props}
          storageScope={storageScope}
          expectedFirebaseUid={expectedFirebaseUid}
        />
      ),
      row: 6,
    },
    needsTeams(selectedRole) && {
      label: t("selectTeam"),
      name: multipleTeams ? "teamIds" : "teamId",
      multiple: multipleTeams,
      disabled: !clubId,
      validators: [
        {
          validator: (value) => {
            const selected = multipleTeams ? value : [value];
            return (
              !selected?.length ||
              selected.some((id) => !teams.some((team) => team._id === id))
            );
          },
          text: (label) => `${label} ${t("cannot_be_empty")}`,
        },
      ],
      options: teams.map((team) => {
        return { text: team.name, value: team._id };
      }),
      renderer: SelectRenderer,
      row: 7,
    },
  ].filter(Boolean);

const initialState = {
  firstName: "",
  lastName: "",
  mobile: "",
  email: "",
  role: "",
  clubId: "",
  teamId: "",
  teamIds: [],
};

const RegisterUserDrawer = ({
  openDrawer,
  onCloseDrawer,
  onExited,
  clubId,
  onRegistered,
  useStaffRegistration = false,
  teams: suppliedTeams,
}) => {
  const { authState, firebaseUser } = useContext(AuthContext);
  const [values, setValues] = useState(initialState);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loadedTeams, setLoadedTeams] = useState({ clubId: null, teams: [] });
  const [pending, setPending] = useState(false);
  const [formVersion, setFormVersion] = useState(0);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const resetForm = useCallback(() => {
    setValues(initialState);
    setFormVersion((version) => version + 1);
    setRegistrationComplete(false);
  }, []);
  const requestVersion = useRef(0);
  const pendingRef = useRef(false);
  const { t } = useTranslation();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();
  const globalSuperAdmin =
    !useStaffRegistration && isSuperAdmin(authState.user);
  const activeClubId = clubId || authState.user?.club?._id || "";
  const selectedClubId = globalSuperAdmin ? values.clubId : activeClubId;
  const teams =
    useStaffRegistration && suppliedTeams !== undefined
      ? suppliedTeams
      : loadedTeams.clubId === selectedClubId
      ? loadedTeams.teams
      : [];
  const identityContextScope = JSON.stringify([
    authState.user?._id,
    authState.user?.roles,
    activeClubId,
    useStaffRegistration,
    firebaseUser?.uid,
  ]);
  const contextScope = JSON.stringify([identityContextScope, openDrawer]);
  // Editing the form must not adopt a new stored account or club before context updates.
  const storageSnapshot = useRef();
  if (storageSnapshot.current?.contextScope !== contextScope) {
    const storage = getStorage();
    storageSnapshot.current = {
      contextScope,
      storageScope: readStaffStorageScope(storage),
      expectedFirebaseUid: storage.getItem("firebaseUid") || "",
    };
  }
  const { storageScope, expectedFirebaseUid } = storageSnapshot.current;
  const accountLookup = useStaffAccountLookup({
    enabled: useStaffRegistration && openDrawer,
    email: values.email, mobile: values.mobile, clubId: selectedClubId,
    storageScope, expectedFirebaseUid,
  });
  const selectedExistingAccount = selectedAccount?.key === accountLookup.key &&
    accountLookup.status === "FOUND" && selectedAccount._id === accountLookup.account?._id
    ? selectedAccount : null;
  const staffIdentityReady = !useStaffRegistration || !!selectedExistingAccount || accountLookup.status === "NOT_FOUND";
  const scope = JSON.stringify([
    contextScope,
    storageScope,
    expectedFirebaseUid,
  ]);
  const formScope = JSON.stringify([
    identityContextScope,
    storageScope,
    expectedFirebaseUid,
  ]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const services = useRef();
  services.current = { axiosGraphQL, errorLog };
  const isImpersonating = Boolean(getStorage().getItem("impersonate"));
  const _schema = schema(
    values.role,
    t,
    globalSuperAdmin,
    teams,
    useStaffRegistration,
    selectedClubId,
    storageScope,
    expectedFirebaseUid
  ).map((field) => ({ ...field, disabled: pending || field.disabled ||
    (!!selectedExistingAccount && ["firstName", "lastName", "email", "mobile"].includes(field.name)) }));
  const identitySchema = _schema.filter((field) =>
    ["firstName", "lastName", "mobile", "email"].includes(field?.name)
  );
  const accessSchema = _schema.filter(
    (field) =>
      field?.name &&
      !["firstName", "lastName", "mobile", "email"].includes(field.name)
  );

  useEffect(() => {
    requestVersion.current += 1;
    pendingRef.current = false;
    setPending(false);
    return () => {
      requestVersion.current += 1;
    };
  }, [scope]);

  // Account/club changes clear immediately; ordinary closing retains the form.
  useEffect(() => {
    resetForm();
    setSelectedAccount(null);
    setLoadedTeams({ clubId: null, teams: [] });
  }, [formScope, resetForm]);

  useEffect(() => {
    // Super Admin can keep registering; Staff closes after its success callback.
    if (registrationComplete && openDrawer) resetForm();
  }, [registrationComplete, openDrawer, resetForm]);

  useEffect(() => {
    if (!openDrawer) return;
    let active = true;
    const isCurrent = () =>
      active && readStaffStorageScope(getStorage()) === storageScope;
    setLoadedTeams({ clubId: null, teams: [] });
    if (
      isCurrent() &&
      expectedFirebaseUid &&
      openDrawer &&
      selectedClubId &&
      !(useStaffRegistration && suppliedTeams !== undefined)
    ) {
      services.current
        .axiosGraphQL(
          {
            query: GET_CLUB_TEAMS,
            variables: {
              clubId: selectedClubId,
              skip: 0,
              limit: 500,
            },
          },
          { expectedFirebaseUid }
        )
        .then((response) => {
          const { ClubTeams } = response.data.data;
          if (isCurrent())
            setLoadedTeams({ clubId: selectedClubId, teams: ClubTeams || [] });
        })
        .catch((error) => {
          if (!isCurrent()) return;
          services.current.errorLog(
            error,
            "RegisterUserDrawer",
            "useEffect",
            "GET_CLUB_TEAMS"
          );
          toast.error(error.message);
        });
    }
    return () => {
      active = false;
    };
  }, [
    openDrawer,
    selectedClubId,
    useStaffRegistration,
    suppliedTeams,
    scope,
    storageScope,
    expectedFirebaseUid,
  ]);

  const getRolesTranslated = (role, isError) => {
    switch (role) {
      case USER_ROLES.CLUB_ADMIN:
        return isError ? t("error_creating_club_admin") : t("the_club_admin");
      case USER_ROLES.TEAM_ADMIN:
        return isError ? t("error_creating_team_admin") : t("the_team_admin");
      case USER_ROLES.COACH:
        return isError ? t("error_creating_coach") : t("the_coach");
    }
  };

  const handleSuccess = (message) => {
    toast.success(message);
    setRegistrationComplete(true);
  };

  const handleError = (error, queryName) => {
    errorLog(error, "RegisterUserDrawer", "handleSubmit", queryName);
    toast.error(error.message);
  };

  const handleSubmit = async () => {
    if (
      pendingRef.current ||
      !openDrawer ||
      readStaffStorageScope(getStorage()) !== storageScope
    )
      return;
    if (
      !expectedFirebaseUid ||
      (firebaseUser?.uid && firebaseUser.uid !== expectedFirebaseUid)
    ) {
      handleError(new Error(t("session_identity_changed")), "REGISTER_USER");
      return;
    }
    if (!staffIdentityReady) return;
    const errors = {
      ...formValidator({ schema: selectedExistingAccount ? accessSchema : _schema, values }),
      ...(!selectedExistingAccount ? getMissingStaffIdentityErrors(values, t) : {}),
    };
    if (Object.keys(errors).length > 0) {
      setValues((prevState) => ({ ...prevState, errors }));
      return;
    }
    if (!selectedClubId) return;
    const query = globalSuperAdmin ? REGISTER_USER_SA : REGISTER_CLUB_USER;
    const queryName = globalSuperAdmin
      ? "REGISTER_USER_SA"
      : "REGISTER_CLUB_USER";
    const variables = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      mobile: values.mobile.trim(),
      role: values.role,
      clubId: selectedClubId,
      ...(useStaffRegistration
        ? { teamIds: needsTeams(values.role) ? values.teamIds : [] }
        : { teamId: needsTeams(values.role) ? values.teamId : "" }),
      ...(useStaffRegistration ? selectedExistingAccount ? {
        existingUserId: selectedExistingAccount._id,
        lookupIdentity: accountLookup.identity,
        firstName: "", lastName: "", email: "", mobile: "",
      } : { requireNewAccount: true } : {}),
    };
    const version = ++requestVersion.current;
    const isCurrent = () =>
      requestVersion.current === version &&
      currentScope.current === scope &&
      readStaffStorageScope(getStorage()) === storageScope;
    pendingRef.current = true;
    setPending(true);
    try {
      const response = await axiosGraphQL(
        { query, variables },
        { expectedFirebaseUid }
      );
      if (!isCurrent()) return;
      const result = response?.data?.data;
      const registeredUser = globalSuperAdmin
        ? result?.RegisterUserSa
        : result?.registerClubUser;
      if (!registeredUser?._id)
        throw new Error(getRolesTranslated(values.role, true));
      handleSuccess(
        `${getRolesTranslated(values.role)} ${t("created_successfully")}`
      );
      onRegistered?.(registeredUser);
    } catch (error) {
      if (isCurrent()) {
        if (useStaffRegistration && error.codes?.includes("STAFF_ACCOUNT_EXISTS")) {
          setSelectedAccount(null);
          accountLookup.refresh();
        }
        handleError(error, queryName);
      }
    } finally {
      if (isCurrent()) {
        pendingRef.current = false;
        setPending(false);
      }
    }
  };

  const onChange = ({ target: { name, value } }) => {
    if (pendingRef.current) return;
    if (name === "email" || name === "mobile") setSelectedAccount(null);
    setValues((prevState) => ({
      ...prevState,
      [name]: name === "mobile" ? value?.replace(/\s/g, "") : value,
      ...(name === "clubId" || name === "role"
        ? { teamId: "", teamIds: [] }
        : {}),
      errors: {},
    }));
  };

  const handleCloseDrawer = () => {
    requestVersion.current += 1;
    pendingRef.current = false;
    setPending(false);
    onCloseDrawer();
  };

  const handleExited = () => {
    if (openDrawer) return;
    resetForm();
    setLoadedTeams({ clubId: null, teams: [] });
    onExited?.();
  };

  return (
    <Drawer
      open={openDrawer}
      onClose={handleCloseDrawer}
      onExited={handleExited}
      size="wide"
      aria-labelledby="register-user-drawer-title"
      closeLabel={t("close")}
    >
      <div className={cn("flex min-h-0 w-full flex-1 flex-col", isImpersonating ? "pt-6" : undefined)}>
        <SheetHeader>
          <SheetTitle id="register-user-drawer-title">{t(useStaffRegistration ? "add_staff" : "register_user")}</SheetTitle>
          <SheetDescription>{t("register_user_description")}</SheetDescription>
        </SheetHeader>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto px-6 pb-6" key={formVersion} data-testid="register-user-drawer-body">
          <div className="flex flex-col gap-4">
            <Card size="sm" role="region" aria-labelledby="user-identity-section-title">
              <CardHeader>
                <CardTitle role="heading" aria-level={3} id="user-identity-section-title">{t("user_identity_title")}</CardTitle>
                <CardDescription>{t("user_identity_description")}</CardDescription>
              </CardHeader>
              <CardContent>
              {useStaffRegistration && <p>{t("staff_lookup_help")}</p>}
              <Form
                schema={identitySchema}
                values={values}
                onChange={onChange}
                errors={values.errors}
              />
                {useStaffRegistration && <div className="mt-4 flex flex-col gap-2" aria-live="polite">
                  {accountLookup.status === "LOADING" && <p>{t("staff_lookup_loading")}</p>}
                  {accountLookup.status === "ERROR" && <StatusAlert severity="error">{t("staff_lookup_error")}</StatusAlert>}
                  {accountLookup.status === "CONFLICT" && <StatusAlert severity="warning">{t("staff_lookup_conflict")}</StatusAlert>}
                  {accountLookup.status === "NOT_FOUND" && <StatusAlert severity="info">{t("staff_lookup_not_found")}</StatusAlert>}
                  {accountLookup.status === "FOUND" && <>
                    <p>{[accountLookup.account.firstName, accountLookup.account.lastName].filter(Boolean).join(" ") || t("staff_lookup_existing")}</p>
                    <p>{accountLookup.identity}</p>
                    <Button type="button" disabled={pending} onClick={() => {
                      setSelectedAccount(selectedExistingAccount ? null : { ...accountLookup.account, key: accountLookup.key });
                      if (!selectedExistingAccount) setValues(previous => ({ ...previous,
                        firstName: accountLookup.account.firstName || "", lastName: accountLookup.account.lastName || "",
                      }));
                    }}>{t(selectedExistingAccount ? "staff_lookup_change" : "staff_lookup_select")}</Button>
                  </>}
                </div>}
            </CardContent>
            </Card>
            <Card size="sm" role="region" aria-labelledby="user-access-section-title">
              <CardHeader>
                <CardTitle role="heading" aria-level={3} id="user-access-section-title">{t("user_access_title")}</CardTitle>
                <CardDescription>{t(
                useStaffRegistration
                  ? "staff_join_instructions"
                  : "user_access_description"
              )}</CardDescription>
              </CardHeader>
              <CardContent>
              <Form
                schema={accessSchema}
                values={values}
                onChange={onChange}
                errors={values.errors}
              />
            </CardContent>
            </Card>
          </div>
        </div>
        <SheetFooter data-testid="register-user-drawer-footer">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button type="button" variant="outline" className="w-full sm:flex-1" onClick={handleCloseDrawer} disabled={pending}>
              {t("cancel")}
            </Button>
            <Button
              type="button"
              className="w-full sm:flex-1"
              onClick={handleSubmit}
              disabled={pending || !openDrawer || !staffIdentityReady || (!globalSuperAdmin && !selectedClubId)}
            >
              <Plus aria-hidden="true" />
              {t(selectedExistingAccount ? "staff_lookup_assign" : "create")}
            </Button>
          </div>
        </SheetFooter>
      </div>
    </Drawer>
  );
};

export default RegisterUserDrawer;
